import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Flex, Group, Loader, MultiSelect, Select, Stack, Stepper, TagsInput, Text, TextInput, ThemeIcon, Title } from "@mantine/core";
import { AppModal } from "../../shared/AppModal";
import { DateInput } from "@mantine/dates";
import { useForm, yupResolver } from "@mantine/form";
import { Assignment, AssignmentOutlined, CalendarMonthOutlined, CalendarToday, CalendarTodayOutlined, Check, CheckCircle, EmailOutlined, Language, LocalHospitalOutlined, LocationOn, MedicalServicesOutlined, PersonOutline, PhoneOutlined, Psychology, PsychologyOutlined, Schedule, ScheduleOutlined, SchoolOutlined, StarOutline, TrendingUpOutlined } from "@mui/icons-material";
import { isSameDay } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { TIMES } from "../../datasets/days_available";
import { GRADE_LEVELS } from "../../datasets/grade_level";
import { PREFERRED_LANGUAGES } from "../../datasets/preferred_languages";
import { FILE_TYPE_OPTIONS } from "../../enums/FileType";
import { useCreateUnifiedPriorityEntryMutation, useGetTherapySubCategoryGroupsQuery, usePriorityEntryModalQuery } from "../../graphql/graphql";
import { useFileUpload } from "../../hooks/useFileUpload";
import { generateVideoThumbnail, getFileTypeFromMimeType } from "../../utils/File.utils";
import { removeNullValues } from "../../utils/Object.utils";
import { formatPhoneNumber } from "../../utils/PhoneNumber.utils";
import { titleCase } from "../../utils/String.utils";
import { PriorityEntryInitialValues, PriorityEntrySchema } from "./PriorityEntryModal.validation";
import { FileUpload } from "../../shared/FileUpload/FileUpload";
import { ImageWithPlaceholder } from "../../shared/ImageWithPlaceholder";
import { DayChipGroup } from "../../shared/Misc/DayChipGroup";
import { showErrorNotification, showSuccessNotification } from "../../shared/Notifications";
import { Confirmation } from "../../shared/Confirmation";
export const PriorityEntryModal = ({ priorityEntryId, opened, close }) => {
    // State
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [formFiles, setFormFiles] = useState([]);
    // Mutations
    const [{ fetching: isCreatingPriorityEntry }, createUnifiedPriorityEntry] = useCreateUnifiedPriorityEntryMutation();
    // Hooks
    const { uploadFiles, isUploadingFile, setFiles: setUploadFiles } = useFileUpload();
    // Form Data
    const form = useForm({
        mode: "controlled",
        initialValues: PriorityEntryInitialValues,
        validate: yupResolver(PriorityEntrySchema),
        validateInputOnChange: false
    });
    const [{ data, fetching: drawerFetching }] = usePriorityEntryModalQuery({
        variables: {
            inOrganization: true
        },
        pause: !opened
    });
    const providers = data?.users?.users ?? [];
    const conditions = data?.conditions?.conditionsList ?? [];
    const insurances = data?.insurances?.insurancesList ?? [];
    const therapyCategories = data?.therapyCategories?.therapyCategoriesList ?? [];
    const priorityStatuses = data?.priorityStatuses?.priorityStatuses ?? [];
    // Get only the therapy sub category groups for the selected therapy category
    const [{ data: therapySubCategoriesData, fetching: therapySubCategoriesFetching }] = useGetTherapySubCategoryGroupsQuery({
        variables: {
            therapyCategoryIds: form.values.patient?.therapyCategoryIds ?? []
        },
        pause: !form.values.patient?.therapyCategoryIds?.length || !opened
    });
    const therapySubCategories = therapySubCategoriesData?.therapySubCategories
        ?.therapySubCategoryGroups ?? [];
    // Memoize handlers to prevent recreation on every render
    const handleEmailChange = useCallback((event) => {
        form.setFieldValue("caregiver.email", event.target.value.trim().toLowerCase());
    }, []);
    const handlePhoneChange = useCallback((event) => {
        const formattedNumber = formatPhoneNumber(event.target.value.trim());
        form.setFieldValue("caregiver.phoneNumber", formattedNumber);
    }, []);
    // File Upload handlers / cleanup
    const cleanupFiles = useCallback(() => {
        formFiles.forEach((f) => URL.revokeObjectURL(f.url));
        setFormFiles([]);
        setUploadFiles([]); // keep useFileUpload's internal file list in sync
    }, [formFiles, setUploadFiles]);
    useEffect(() => {
        // Cleanup URLs on unmount
        return () => {
            formFiles.forEach((f) => URL.revokeObjectURL(f.url));
        };
    }, [formFiles]);
    const handleSetFiles = useCallback(async (newFiles) => {
        // Store raw files into useFileUpload hook state too (so its files/setFiles are used)
        setUploadFiles((prev) => [...prev, ...newFiles]);
        const entries = await Promise.all(newFiles.map(async (file) => {
            let thumbnailUrl;
            if (file.type.startsWith("video/")) {
                try {
                    thumbnailUrl = await generateVideoThumbnail(file);
                }
                catch (e) {
                    console.warn("Could not generate video thumbnail", e);
                }
            }
            return {
                file,
                url: URL.createObjectURL(file),
                thumbnailUrl,
                name: file.name
                    .replace(/\.[^/.]+$/, "")
                    .replace(/[^a-zA-Z0-9]/g, " "),
                fileType: getFileTypeFromMimeType(file.type)
            };
        }));
        setFormFiles((prev) => [...prev, ...entries]);
    }, [setUploadFiles]);
    const removeFile = useCallback((index) => {
        setFormFiles((prev) => {
            const next = [...prev];
            URL.revokeObjectURL(next[index].url);
            next.splice(index, 1);
            return next;
        });
        // NOTE: we intentionally do not attempt to remove from useFileUpload.files,
        // since we only use that list for "hook usage" / potential metadata helpers.
    }, []);
    const updateFileField = useCallback((index, field, value) => {
        setFormFiles((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    }, []);
    // Submits the Priority Entry to be created or updated
    const submitPriorityEntry = async (event) => {
        event.preventDefault();
        const valid = form.validate();
        if (valid.hasErrors)
            return;
        const formValues = form.getValues();
        removeNullValues(formValues);
        try {
            const response = await createUnifiedPriorityEntry({
                input: formValues
            });
            if (!response.data?.priorityEntries?.createUnifiedPriorityEntry
                ?.length) {
                showErrorNotification("Failed to create pipeline entry");
                throw new Error("Failed to create pipeline entry");
            }
            else {
                const createdEntry = response.data?.priorityEntries
                    ?.createUnifiedPriorityEntry?.[0] ?? null;
                const caregiverId = createdEntry?.caregiver?.userId ?? null;
                const patientId = createdEntry?.patient?.patientId ?? null;
                // Upload attached files (if any) after the entry is created
                if (formFiles.length) {
                    if (!caregiverId || !patientId) {
                        showErrorNotification("Pipeline entry created, but missing caregiver/patient IDs for file upload");
                    }
                    else {
                        const uploads = formFiles.map((f) => ({
                            file: f.file,
                            name: f.name || f.file.name,
                            fileType: f.fileType,
                            tags: [],
                            isShared: false,
                            caregiverId,
                            patientId
                        }));
                        await uploadFiles(uploads);
                    }
                }
                showSuccessNotification(`Pipeline entry created successfully`);
                // Reset form dirty state after successful submission
                form.reset();
                form.resetDirty();
                setActiveStep(0);
                cleanupFiles();
                handleClose(false);
            }
        }
        catch (error) {
            console.error("An error occurred.", error);
            showErrorNotification();
        }
    };
    // Handles the closing of the drawer
    const handleClose = (shouldConfirmLeave = true) => {
        if ((form.isDirty() || formFiles.length > 0) && shouldConfirmLeave) {
            setIsConfirmationModalOpen(true);
            return;
        }
        setIsConfirmationModalOpen(false);
        setActiveStep(0);
        form.reset();
        cleanupFiles();
        close();
    };
    // Steps for the stepper
    const steps = [
        {
            label: "Primary Contact",
            icon: _jsx(PersonOutline, {}),
            error: [
                "caregiver.firstName",
                "caregiver.lastName",
                "caregiver.email",
                "caregiver.phoneNumber"
            ]
        },
        {
            label: "Client Details",
            icon: _jsx(Assignment, {}),
            error: [
                "patient.firstName",
                "patient.lastName",
                "patient.dateOfBirth",
                "patient.postalCode",
                "therapyCategoryIds"
            ]
        },
        {
            label: "Preferences",
            icon: _jsx(Schedule, {})
        },
        {
            label: "Additional",
            icon: _jsx(Psychology, {}),
            error: ["inquiredAt"]
        }
    ];
    return (_jsxs(AppModal, { variant: "stepper", title: `${priorityEntryId ? "Update" : "Add"} Pipeline Entry`, size: "31rem", opened: opened, onClose: handleClose, children: [_jsxs(Stack, { h: "100%", justify: "space-between", children: [_jsxs(Stack, { children: [_jsx(Stepper, { size: "xs", allowNextStepsSelect: true, color: "var(--mantine-color-primary-9)", style: { marginBottom: "1.5rem" }, active: activeStep, onStepClick: setActiveStep, children: steps.map((step, index) => {
                                    const hasErrors = step.error?.some((error) => form.errors[error]);
                                    return (_jsx(Stepper.Step, { 
                                        // label={step.label}
                                        icon: step.icon, color: hasErrors
                                            ? "var(--mantine-color-red-5)"
                                            : undefined }, index));
                                }) }), activeStep === 0 && (_jsxs(Stack, { gap: "sm", children: [_jsx(Title, { order: 5, ta: "center", children: "Primary Contact" }), _jsx(TextInput, { "data-autofocus": true, required: true, flex: 1, label: "First Name", placeholder: "First Name", leftSection: _jsx(PersonOutline, {}), ...form.getInputProps("caregiver.firstName") }), _jsx(TextInput, { required: true, flex: 1, label: "Last Name", placeholder: "Last Name", leftSection: _jsx(PersonOutline, {}), ...form.getInputProps("caregiver.lastName") }), _jsx(TextInput, { required: true, flex: 1, label: "Email", placeholder: "Email", leftSection: _jsx(EmailOutlined, {}), ...form.getInputProps("caregiver.email"), onChange: handleEmailChange }), _jsx(TextInput, { required: true, flex: 1, label: "Phone", placeholder: "Phone", leftSection: _jsx(PhoneOutlined, {}), ...form.getInputProps("caregiver.phoneNumber"), onChange: handlePhoneChange }), _jsx(MultiSelect, { variant: "pill", label: "Insurances", placeholder: "What insurances is this client covered by?", leftSection: _jsx(AssignmentOutlined, {}), limit: 100, maxValues: 5, searchable: true, clearable: true, rightSection: drawerFetching ? _jsx(Loader, { size: 16 }) : null, data: insurances, ...form.getInputProps("patient.insuranceIds") })] })), activeStep === 1 && (_jsxs(Stack, { gap: "xs", children: [_jsx(Title, { order: 5, ta: "center", children: "Client Details" }), _jsx(TextInput, { required: true, label: "First Name", placeholder: "First Name", flex: 1, leftSection: _jsx(PersonOutline, {}), ...form.getInputProps("patient.firstName") }), _jsx(TextInput, { required: true, label: "Last Name", placeholder: "Last Name", flex: 1, leftSection: _jsx(PersonOutline, {}), ...form.getInputProps("patient.lastName") }), _jsx(DateInput, { required: true, allowDeselect: true, flex: 1, firstDayOfWeek: 0, label: "Date of Birth", placeholder: "Date of Birth", valueFormat: "MMM DD, YYYY", defaultDate: new Date(), clearable: true, leftSection: _jsx(CalendarToday, {}), error: form.errors["patient.dateOfBirth"], value: form.values.patient?.dateOfBirth
                                            ? new Date(form.values.patient.dateOfBirth)
                                            : null, onChange: (d) => form.setFieldValue("patient.dateOfBirth", d ? d.toISOString() : ""), getDayProps: (date) => {
                                            return {
                                                style: {
                                                    backgroundColor: isSameDay(date, new Date())
                                                        ? "var(--mantine-color-primary-7)"
                                                        : "inherit"
                                                }
                                            };
                                        } }), _jsx(TextInput, { required: true, flex: 1, label: "Zip Code", placeholder: "Zip Code", leftSection: _jsx(LocationOn, {}), maxLength: 5, ...form.getInputProps("patient.postalCode") }), _jsx(MultiSelect, { variant: "pill", required: true, label: "Therapies Needed", placeholder: "Choose from therapies offered by this organization", searchable: true, clearable: true, leftSection: _jsx(MedicalServicesOutlined, {}), rightSection: drawerFetching ? _jsx(Loader, { size: 16 }) : null, data: therapyCategories, ...form.getInputProps("patient.therapyCategoryIds"), onChange: (value) => {
                                            form.setFieldValue("patient.therapyCategoryIds", value);
                                            form.setFieldValue("patient.therapySubCategoryIds", []);
                                        } }), _jsx(MultiSelect, { variant: "pill", label: "Skills / Interventions Needed", placeholder: "Choose from skills / interventions offered by this organization", searchable: true, clearable: true, disabled: !form.values.patient?.therapyCategoryIds
                                            ?.length, leftSection: _jsx(PsychologyOutlined, {}), rightSection: therapySubCategoriesFetching ? (_jsx(Loader, { size: 16 })) : null, data: therapySubCategories, ...form.getInputProps("patient.therapySubCategoryIds") }), _jsx(MultiSelect, { variant: "pill", label: "Diagnoses", placeholder: "What conditions does this client have?", searchable: true, clearable: true, leftSection: _jsx(LocalHospitalOutlined, {}), rightSection: drawerFetching ? _jsx(Loader, { size: 16 }) : null, data: conditions, ...form.getInputProps("patient.conditionIds") })] })), activeStep === 2 && (_jsxs(Stack, { gap: "xs", children: [_jsx(Title, { order: 5, ta: "center", children: "Preferences" }), _jsxs(Stack, { gap: "xs", children: [_jsxs(Flex, { align: "center", gap: "sm", children: [_jsx(ThemeIcon, { variant: "light", bg: "transparent", color: "gray", children: _jsx(CalendarTodayOutlined, {}) }), _jsx(Text, { variant: "body", size: "sm", children: "Days Available" })] }), _jsx(DayChipGroup, { abbreviate: true, size: "sm", value: form.values.patient?.daysAvailable?.map((d) => titleCase(d)), onChipChange: (v) => form.setFieldValue("patient.daysAvailable", v) })] }), _jsx(MultiSelect, { variant: "pill", label: "Time Available", placeholder: "Time Available", leftSection: _jsx(ScheduleOutlined, {}), flex: 1, clearable: true, searchable: true, checkIconPosition: "right", data: TIMES, ...form.getInputProps("patient.timeAvailable") }), _jsx(MultiSelect, { variant: "pill", label: "Preferred Languages", placeholder: "Preferred Languages", leftSection: _jsx(Language, {}), flex: 1, data: PREFERRED_LANGUAGES, clearable: true, searchable: true, ...form.getInputProps("patient.preferredLanguages") })] })), activeStep === 3 && (_jsxs(Stack, { gap: "xs", children: [_jsx(Title, { order: 5, ta: "center", children: "Additional" }), _jsx(MultiSelect, { variant: "pill", label: "Assigned Providers", placeholder: "Assigned Providers", data: providers.map((provider) => ({
                                            value: provider.userId,
                                            label: `${provider.firstName} ${provider.lastName}`
                                        })), renderOption: (option) => {
                                            const imageUrl = providers.find((provider) => provider.userId ===
                                                option.option.value)?.imageUrl;
                                            return (_jsxs(Flex, { align: "center", gap: "xs", children: [form.values.patient?.providerIds?.includes(option.option.value) && (_jsx(Check, { style: {
                                                            fontSize: "1rem",
                                                            color: "var(--mantine-color-gray-5)"
                                                        } })), _jsx(ImageWithPlaceholder, { imageUrl: imageUrl, avatarText: option.option.label, size: 24 }), _jsx(Text, { variant: "body", children: option.option.label })] }));
                                        }, clearable: true, searchable: true, ...form.getInputProps("patient.providerIds") }), _jsx(DateInput, { required: true, allowDeselect: true, firstDayOfWeek: 0, label: "Inquiry Date", placeholder: "Inquiry Date", defaultDate: new Date(), leftSection: _jsx(CalendarMonthOutlined, {}), flex: 1, clearable: true, value: form.values.inquiredAt
                                            ? new Date(form.values.inquiredAt)
                                            : null, onChange: (d) => form.setFieldValue("inquiredAt", d ? d.toISOString() : ""), getDayProps: (date) => {
                                            return {
                                                style: {
                                                    backgroundColor: isSameDay(date, new Date())
                                                        ? "var(--mantine-color-primary-7)"
                                                        : "inherit"
                                                }
                                            };
                                        } }), _jsx(Select, { label: "Grade Level", placeholder: "Grade Level", leftSection: _jsx(SchoolOutlined, {}), flex: 1, clearable: true, searchable: true, data: GRADE_LEVELS, ...form.getInputProps("patient.gradeLevel") }), _jsx(Select, { label: "Referral Stage", placeholder: "What referral stage is this entry in?", leftSection: _jsx(TrendingUpOutlined, {}), flex: 1, clearable: true, searchable: true, checkIconPosition: "right", data: priorityStatuses
                                            .map((status) => {
                                            if (!status)
                                                return null;
                                            return {
                                                value: status.priorityStatusId,
                                                label: status.name
                                            };
                                        })
                                            .filter((status) => status !== null), styles: {
                                            option: {
                                                padding: 1
                                            }
                                        }, renderOption: (option) => {
                                            const statusColor = priorityStatuses.find((status) => status?.priorityStatusId ===
                                                option.option.value)?.colorHex;
                                            return (_jsx("div", { style: {
                                                    width: "100%",
                                                    height: "100%"
                                                }, children: _jsx(Text, { variant: "body", size: "sm", style: {
                                                        backgroundColor: statusColor,
                                                        paddingTop: 4,
                                                        paddingBottom: 4,
                                                        paddingLeft: 10,
                                                        borderRadius: 4
                                                    }, children: option.option.label }) }));
                                        }, ...form.getInputProps("priorityStatusId") }), _jsx(TagsInput, { variant: "pill", label: "Tags", placeholder: "How would you like to tag this entry?", leftSection: _jsx(StarOutline, {}), flex: 1, clearable: true, acceptValueOnBlur: true, splitChars: [","], ...form.getInputProps("tags") }), _jsx(FileUpload, { fileTypeOptions: FILE_TYPE_OPTIONS, isEditing: false, files: formFiles, setFiles: handleSetFiles, onRemove: removeFile, onUpdate: updateFileField })] }))] }), _jsx(Stack, { gap: "sm", children: _jsxs(Group, { justify: "space-between", align: "center", style: {
                                paddingTop: "1rem",
                                borderTop: "1px solid color-mix(in srgb, var(--mantine-color-black) 10%, transparent)"
                            }, children: [activeStep === 0 && (_jsx(Button, { variant: "outline", onClick: () => handleClose(), children: "Close" })), activeStep > 0 && (_jsx(Button, { variant: "outline", onClick: () => setActiveStep(activeStep - 1), children: "Previous" })), activeStep < steps.length - 1 ? (_jsx(Button, { variant: "filled", onClick: () => setActiveStep(activeStep + 1), disabled: activeStep === steps.length - 1, children: "Next Step" })) : ((!priorityEntryId) && (_jsx(Button, { variant: "filled", leftSection: _jsx(CheckCircle, { fontSize: "inherit" }), loading: drawerFetching ||
                                        isCreatingPriorityEntry ||
                                        isUploadingFile, onClick: submitPriorityEntry, children: priorityEntryId
                                        ? "Update Entry"
                                        : "Create Entry" })))] }) })] }), _jsx(Confirmation, { action: "leave", additionalText: "You have unsaved changes, that won't be saved.", opened: isConfirmationModalOpen, close: () => setIsConfirmationModalOpen(false), handleAction: async () => {
                    handleClose(false);
                } })] }));
};
